import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { api } from '@/controllers/api';

export const AdminLayout = ({ children }: { children: React.ReactNode }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingTestimonials, setPendingTestimonials] = useState(0);
    const [activeUsers, setActiveUsers] = useState<number | null>(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [userRole, setUserRole] = useState<string>('admin');

    const isGuest = userRole === 'guest';

    // Fermer le menu quand on change de page
    useEffect(() => {
        setSidebarOpen(false);
    }, [location.pathname]);

    // Récupérer le rôle de l'utilisateur connecté
    useEffect(() => {
        api.auth.me()
            .then(({ user }) => { if (user?.role) setUserRole(user.role); })
            .catch(() => {});
    }, []);

    // Fetch unread messages count on mount and periodically (admin uniquement)
    useEffect(() => {
        if (isGuest) return;

        const fetchUnreadCount = async () => {
            try {
                const messages = await api.messages.list();
                const unread = messages.filter(m => m.status === 'unread').length;
                setUnreadCount(unread);
            } catch (error) {
                console.error('Failed to fetch messages:', error);
            }
        };

        fetchUnreadCount();
        // Refresh every 60 seconds
        const interval = setInterval(fetchUnreadCount, 60000);
        return () => clearInterval(interval);
    }, [isGuest]);

    // Fetch pending testimonials count (admin uniquement)
    useEffect(() => {
        if (isGuest) return;

        const fetchPendingTestimonials = async () => {
            try {
                const { count } = await api.testimonials.countPending();
                setPendingTestimonials(count);
            } catch (error) {
                console.error('Failed to fetch pending testimonials:', error);
            }
        };

        fetchPendingTestimonials();
        const interval = setInterval(fetchPendingTestimonials, 60000);
        return () => clearInterval(interval);
    }, [isGuest]);

    // Fetch active users count on mount and periodically
    useEffect(() => {
        const fetchActiveUsers = async () => {
            try {
                const response = await fetch('/api/admin/active-users', { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    setActiveUsers(data.count);
                }
            } catch (error) {
                console.error('Failed to fetch active users:', error);
            }
        };

        fetchActiveUsers();
        // Refresh every 15 seconds
        const interval = setInterval(fetchActiveUsers, 15000);
        return () => clearInterval(interval);
    }, []);

    const allNavItems = [
        { icon: 'analytics', label: 'Statistiques', to: '/admin/statistiques', guestAllowed: true },
        { icon: 'newspaper', label: 'Actualités', to: '/admin/actualites' },
        { icon: 'mode_night', label: 'Événements Astro', to: '/admin/astro-events' },
        { icon: 'workspace_premium', label: 'Abonnés Sentinel', to: '/admin/abonnes' },
        { icon: 'photo_library', label: 'Galerie', to: '/admin/galerie' },
        { icon: 'camera', label: 'Matériel', to: '/admin/materiel' },
        { icon: 'ondemand_video', label: 'Vidéos', to: '/admin/videos' },
        { icon: 'folder_open', label: 'Ressources', to: '/admin/ressources' },
        { icon: 'person', label: 'Profil', to: '/admin/profil' },
        { icon: 'mail', label: 'Messages', to: '/admin/messages', badge: unreadCount },
        { icon: 'rate_review', label: 'Témoignages', to: '/admin/temoignages', badge: pendingTestimonials },
        { icon: 'settings', label: 'Paramètres', to: '/admin/parametres' },
        { icon: 'history', label: 'Versions Planner', to: '/api/admin/planner-management-ui', isExternal: true },
    ];

    const navItems = isGuest ? allNavItems.filter(item => item.guestAllowed) : allNavItems;

    const handleLogout = async () => {
        try {
            await api.auth.logout();
            navigate('/admin/login');
        } catch (e) {
            console.error('Logout failed', e);
            // On redirige quand même pour ne pas bloquer l'user
            navigate('/admin/login');
        }
    };

    return (
        <div className="min-h-screen bg-[#0d1117] flex text-slate-300">
            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#161b22] border-b border-slate-800 flex items-center justify-between px-4 z-40">
                <button
                    onClick={() => setSidebarOpen(!sidebarOpen)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                    <span className="material-symbols-outlined text-white">
                        {sidebarOpen ? 'close' : 'menu'}
                    </span>
                </button>
                <span className="font-bold text-white text-sm">SSI Admin</span>
                <div className="flex items-center gap-1">
                    {activeUsers !== null && (
                        <div className="flex items-center gap-1 text-xs text-zinc-400">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                            </span>
                            <span className="text-white font-bold">{activeUsers}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Overlay for mobile sidebar */}
            {sidebarOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-black/60 z-40 backdrop-blur-sm"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed lg:sticky top-0 left-0 h-screen z-50
                w-64 bg-[#161b22] border-r border-slate-800 flex-shrink-0 flex flex-col
                transform transition-transform duration-300 ease-in-out
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            `}>
                <div className="h-16 flex items-center justify-between px-6 border-b border-slate-800">
                    <span className="font-bold text-white">StellarSkyImager Admin</span>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-1 rounded hover:bg-white/10"
                    >
                        <span className="material-symbols-outlined text-zinc-400">close</span>
                    </button>
                </div>

                {/* Active Users Widget */}
                <div className="px-4 py-3 border-b border-slate-800 bg-slate-800/30">
                    <div className="flex items-center gap-2 text-sm">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                        </span>
                        <span className="text-zinc-400">
                            {activeUsers !== null ? (
                                <>
                                    <span className="text-white font-bold">{activeUsers}</span>
                                    {' visiteur'}{activeUsers !== 1 ? 's' : ''} en ligne
                                </>
                            ) : (
                                <span className="text-zinc-500">Chargement...</span>
                            )}
                        </span>
                    </div>
                </div>

                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-4 py-3 rounded-lg text-primary hover:bg-white/5 transition-colors mb-4 border border-primary/20 bg-primary/5"
                    >
                        <span className="material-symbols-outlined mr-3">public</span>
                        Voir le site
                        <span className="material-symbols-outlined ml-auto text-sm">open_in_new</span>
                    </a>
                    {navItems.map((item: any) => (
                        item.isExternal ? (
                            <a
                                key={item.to}
                                href={item.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center px-4 py-3 rounded-lg hover:bg-white/5 transition-colors text-cyan-500/80 hover:text-cyan-400"
                            >
                                <span className="material-symbols-outlined mr-3">{item.icon}</span>
                                {item.label}
                                <span className="material-symbols-outlined ml-auto text-sm">open_in_new</span>
                            </a>
                        ) : (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`flex items-center px-4 py-3 rounded-lg hover:bg-white/5 transition-colors ${location.pathname === item.to ? 'bg-white/10 text-white' : ''
                                    }`}
                            >
                                <span className="material-symbols-outlined mr-3">{item.icon}</span>
                                {item.label}
                                {/* Badge for unread messages */}
                                {item.badge > 0 && (
                                    <span className="ml-auto px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full animate-pulse">
                                        {item.badge}
                                    </span>
                                )}
                            </Link>
                        )
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-800">
                    <button onClick={handleLogout} className="flex items-center px-4 py-2 text-red-400 hover:text-red-300 w-full transition-colors">
                        <span className="material-symbols-outlined mr-3">logout</span>
                        Déconnexion
                    </button>
                </div>
            </aside>
            {/* Main Content */}
            <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
                <div className="p-4 sm:p-6 lg:p-8">
                    {children}
                </div>
            </main>
        </div>
    );
};
